import { useCallback, useEffect, useMemo } from "react";
import { Alert, darken, Paper, Stack, Typography } from "@mui/material";
import {
  DataGridPro as DataGrid,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridColDef,
  GridExpandMoreIcon,
  GridRowClassNameParams,
  GridRowParams,
} from "@mui/x-data-grid-pro";

import __ from "src/utils/Translations";
import { useDataGridQueryWaitingRooms } from "src/Pages/Company/Pending/PendingBenefits/query";
import {
  FilterModel,
  IGroupedWaitingRoom,
} from "src/Pages/Company/Pending/PendingBenefits/types";
import { PendingBenefitsDetails } from "src/Pages/Company/Pending/PendingBenefits/Details/PendingBenefitsDetails";
import { PendingBenefitsToolbar } from "src/Pages/Company/Pending/PendingBenefits/Toolbar/PendingBenefitsToolbar";
import { useSelectedRows } from "src/Pages/Company/Pending/PendingBenefits/SelectedRowsProvider/hooks";
import { SELECTED_ROW_CLASS } from "src/Pages/Company/Pending/PendingBenefits/const";
import { GridDetailPanelToggleColHeader } from "./GridDetailPanelToggleColHeader";
import { CustomCellCheckbox } from "./CustomCellCheckbox";
import { CustomHeaderCheckbox } from "./CustomHeaderCheckbox";
import { CellEmployee } from "./CellEmployee";

export const PendingBenefitsTable = () => {
  const { resetSelection } = useSelectedRows();

  const {
    filters,
    setFilters,
    sortModel,
    setSortModel,
    paginationModel,
    setPaginationModel,
    rows,
    rowCount,
    isFetching,
    isSuccess,
    isError,
    error,
    refetch,
  } = useDataGridQueryWaitingRooms();

  useEffect(() => {
    if (!isFetching && isSuccess) {
      resetSelection();
    }
  }, [isFetching, isSuccess, resetSelection]);

  const columns = useMemo<GridColDef<IGroupedWaitingRoom>[]>(
    () => [
      {
        ...GRID_CHECKBOX_SELECTION_COL_DEF,
        renderCell: (params) => <CustomCellCheckbox {...params} />,
        renderHeader: (params) => <CustomHeaderCheckbox {...params} />,
      },
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        renderHeader: () => <GridDetailPanelToggleColHeader />,
        width: 56,
        cellClassName: "cell-with-expand",
      },
      {
        headerName: __("Pracownik"),
        field: "employee",
        display: "flex",
        renderCell: ({ row }) => <CellEmployee employee={row.employee} />,
        filterable: false,
        sortable: false,
        width: 300,
      },
      {
        headerName: __("Jednostka organizacyjna"),
        field: "employee.organizationUnitName",
        valueGetter: (_value, row) => row.employee.organizationUnitName,
        filterable: false,
        sortable: false,
        width: 200,
      },
      {
        headerName: __("Liczba świadczeń"),
        field: "benefitsCount",
        type: "number",
        filterable: false,
        sortable: false,
        width: 170,
      },
      {
        headerName: __("Ostatnie zamówienie"),
        field: "date",
        valueFormatter: (value?: string) =>
          value && new Date(value).toLocaleDateString("pl-PL"),
        filterable: false,
        sortable: false,
        width: 180,
      },
    ],
    []
  );

  const { getRowState } = useSelectedRows();
  const getRowClassName = useCallback(
    (params: GridRowClassNameParams<IGroupedWaitingRoom>) => {
      const { isChecked } = getRowState(params.row);

      return isChecked ? SELECTED_ROW_CLASS : "";
    },
    [getRowState]
  );

  return (
    <Paper
      sx={{
        padding: "1rem",
      }}
    >
      {isError && (
        <Alert variant="outlined" color="warning" sx={{ mb: 2 }}>
          {String(error)}
        </Alert>
      )}
      <DataGrid
        columns={columns}
        getRowId={getRowId}
        rows={rows}
        rowCount={rowCount}
        loading={isFetching}
        disableColumnFilter
        disableColumnPinning
        disableColumnSorting
        disableRowSelectionOnClick
        disableVirtualization
        disableColumnMenu
        disableEval
        checkboxSelection
        checkboxSelectionVisibleOnly
        getDetailPanelHeight={getDetailPanelHeight}
        getDetailPanelContent={getDetailPanelContent}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 25, 50, 100]}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        filterMode="server"
        hideFooterSelectedRowCount
        showToolbar
        getRowClassName={getRowClassName}
        slots={{
          detailPanelExpandIcon: GridExpandMoreIcon,
          detailPanelCollapseIcon: GridExpandMoreIcon,
          toolbar: PendingBenefitsToolbar,
        }}
        slotProps={{
          loadingOverlay: {
            variant: "linear-progress",
            noRowsVariant: "circular-progress",
          },
          toolbar: {
            filters,
            setFilters,
            refetch,
          },
        }}
        sx={(theme) => ({
          "& .MuiDataGrid-detailPanelToggleCell--expanded > svg": {
            transform: "rotate(180deg)",
          },
          "& .cell-with-expand": {
            textOverflow: "unset",
          },
          [`"& .${SELECTED_ROW_CLASS}"`]: {
            backgroundColor: "blue.50",
            "&:hover": {
              backgroundColor: darken(theme.palette.blue["50"], 0.05),
            },
          },
        })}
      />
    </Paper>
  );
};

const getRowId = (row: IGroupedWaitingRoom): string => row.employee.id;
const getDetailPanelHeight = (): "auto" => "auto";

const getDetailPanelContent = (params: GridRowParams<IGroupedWaitingRoom>) => (
  <PendingBenefitsDetails row={params.row} />
);

declare module "@mui/x-data-grid" {
  interface ToolbarPropsOverrides {
    filters: FilterModel;
    setFilters: React.Dispatch<React.SetStateAction<FilterModel>>;
    refetch: VoidFunction;
  }
}
